// LayerMenu.js
import React, { useState, useRef } from 'react';
import { HexAlphaColorPicker } from "react-colorful";
import { FaEye, FaEyeSlash, FaCog, FaPlus, FaArrowDown, FaTimes, FaEraser, FaTrash, FaFolderPlus, FaMinus } from 'react-icons/fa';
import { TbArrowBigLeftLines } from 'react-icons/tb';
import { FaArrowsTurnToDots } from 'react-icons/fa6';
import ObjectID from 'bson-objectid';

import LayerThumbnail from './LayerThumbnail';
import useDragAndDropSort from '../../../hooks/interface/useDragAndDropSort';
import useStore from '../../../store';
import './LayerMenu.css';

function LayerMenu({ store }) {
  useStore(state => state.render);

  const { ref } = store;
  const { menu, work, info, brush, convy, drawing, premium } = ref;
  const initialAllLayersInfoRef = useRef(JSON.parse(JSON.stringify(convy.layerInfo)));
  const initialLayerOrderRef =  useRef([]);
  const initialLayerGroupsRef =  useRef({});
  const initialBackgroundColorRef = useRef(info.game.settings.backgroundColor || '#FFFFFFFF');

  const { user } = info;
  const { showTemporaryHint, createBetterClick, delayPremiumMenu, closeMenu, showPopupMessage } = menu.methods;
  const { saveUserGameSets, getUserGameSets, saveGameSets, debounceGameSets, saveUserSets } = work.methods;
  const { redraw } = drawing.methods;

  const [userColorView, setUserColorView] = useState(false);
  const [viewedColor, setViewedColor] = useState(info.game.settings.backgroundColor || '#FFFFFFFF');
  const [showDeletedLayers, setShowDeletedLayers] = useState(false);
  const layerMenuRef = useRef(null);
  const deleteZoneRef = useRef(null);
  const ungroupZoneRef = useRef(null);
  const ungroupZoneTopRef = useRef(null);
  const [isOverDeleteZone, setIsOverDeleteZone] = useState(false);
  const [isOverUngroupZone, setIsOverUngroupZone] = useState(false);
  const [isOverUngroupZoneTop, setIsOverUngroupZoneTop] = useState(false);

  const { activeLayerId = 'base' } = getUserGameSets();

  // Получаем настройки игры для групп слоев
  const gameSettings = info.game.settings || {};
  const layerGroups = gameSettings.layerGroups || {};
  const layerOrder = gameSettings.layerOrder || [];


  // Функция совместимости с ранними версиями - составляет layerOrder на основе layer.order
  const ensureLayerOrderCompatibility = () => {
    const currentLayerOrder = gameSettings.layerOrder || [];
    
    // Если layerOrder пуст, создаем его заново
    
    if (currentLayerOrder.length === 0) {

      // Сортируем все слои по старому order
      const sortedLayers = Object.values(convy.layerInfo).sort((a, b) => b.order - a.order);
      
      // Создаем новый layerOrder
      const newLayerOrder = sortedLayers.map(layer => layer.id);
      
      // Добавляем группы (если есть) в начало
      const groupIds = Object.keys(layerGroups);
      const finalLayerOrder = [...groupIds, ...newLayerOrder];
      
      saveGameSets({ layerOrder: finalLayerOrder });
      return finalLayerOrder;
    }
    
    return currentLayerOrder;
  };

  // Функция проверки что все слои попали в layerOrder
  const ensureAllLayersInOrder = () => {
    
    const currentLayerOrder = gameSettings.layerOrder || [];
    const allLayerIds = Object.keys(convy.layerInfo).filter(id => !convy.layerInfo[id].deleted);
    const missingLayers = allLayerIds.filter(id => !currentLayerOrder.includes(id));
    
    if (missingLayers.length > 0) {
      // Добавляем недостающие слои в конец
      const newLayerOrder = [...currentLayerOrder, ...missingLayers];
      saveGameSets({ layerOrder: newLayerOrder });
      return newLayerOrder;
    }
    
    return currentLayerOrder;
  };

  // Обеспечиваем совместимость при загрузке компонента
  React.useEffect(() => {
    ensureLayerOrderCompatibility();
    ensureAllLayersInOrder();
    initialLayerOrderRef.current = getActualLayerOrder();
    initialLayerGroupsRef.current = JSON.parse(JSON.stringify(layerGroups));
  }, []);

  // Получаем актуальный layerOrder с проверками
  const getActualLayerOrder = () => {
    let actualOrder = ensureLayerOrderCompatibility();
    actualOrder = ensureAllLayersInOrder();
    return actualOrder;
  };

  // Функция для получения настроек слоя (аналогично customBrushSetsFun)
  const getLayerSettings = (layerId) => {
    const layer = convy.layerInfo[layerId];
    if (!layer) return {};
    return {
      groupId: layer.groupId || null,
      ...layer
    };
  };

  // Функция для сохранения настроек слоя
  const saveLayerSettings = (layerId, settings) => {
    if (convy.layerInfo[layerId]) {
      Object.assign(convy.layerInfo[layerId], settings);
      saveGameSets({ layerInfo: convy.layerInfo });
    }
  };

  // Подсчет слоев в группах
  const layersInGroupsCount = () => {
    const counts = {};
    allLayersArray.forEach(layer => {
      // Считаем только основные слои, не маски
      if (!layer.clippingFor) {
        const settings = getLayerSettings(layer.id);
        if (settings.groupId && layerGroups[settings.groupId] && !layer.deleted) {
          if (!counts[settings.groupId]) {
            counts[settings.groupId] = 0;
          }
          counts[settings.groupId]++;
        }
      }
    });
    return counts;
  };

  // Создание группы слоев
  const handleCreateLayerGroup = () => {
    if (!premium.premiumFeatures.includes('layers') || user.premiumOff) {
      delayPremiumMenu('menu');
      return;
    }

    showPopupMessage({
      title: window.t('layer_menu.group_create_title'),
      text: window.t('layer_menu.group_create_text'),
      input: {
        placeholder: window.t('layer_menu.group_create_placeholder'),
      },
      buttons: [
        { type: 'cancel', text: window.t('customize.button_cancel') },
        { id: 'submit', type: 'submit', text: window.t('customize.button_submit') },
      ],
      callback: (buttonId, inputValue) => {
        if (buttonId === 'submit' && inputValue) {
          window.croco?.haptic?.();
          const newGroupId = new ObjectID().toHexString();
          const newGroup = {
            title: inputValue,
            expanded: true,
          };

          const newLayerGroups = { ...layerGroups, [newGroupId]: newGroup };
          
          // Получаем актуальный порядок и добавляем новую группу в начало
          const currentLayerOrder = getActualLayerOrder();
          const newLayerOrder = [newGroupId, ...currentLayerOrder];

          saveGameSets({
            layerGroups: newLayerGroups,
            layerOrder: newLayerOrder
          });
          menu.methods.render();
        }
      }
    });
  };

  // Переключение раскрытия группы
  const handleToggleLayerGroup = (groupId) => {
    const groups = { ...layerGroups };
    if (groups[groupId]) {
      groups[groupId] = {
        ...groups[groupId],
        expanded: !groups[groupId].expanded,
      };
      saveGameSets({ layerGroups: groups });
      menu.methods.render();
    }

  };

  // Переключение видимости группы
  const toggleGroupVisibility = (groupId) => {
    brush.lastActionTime = Date.now();
    const groups = { ...layerGroups };
    if (groups[groupId]) {
      groups[groupId] = {
        ...groups[groupId],
        visible: groups[groupId].visible !== false ? false : true
      };
      saveGameSets({ layerGroups: groups });
      showTemporaryHint(
        window.t(groups[groupId].visible ? 'layer_menu.group_visible' : 'layer_menu.group_invisible', { groupName: groups[groupId].title }),
        { force: true }
      );
      redraw();
      menu.methods.appleHaptic?.('light');
    }
  };

  // Структурирование слоев с учетом групп
  const structureLayersWithGroups = (layers, showDeleted = false) => {
    const actualLayerOrder = getActualLayerOrder();
    
    const applySortOrder = (items, orderArray) => {
      if (!orderArray || orderArray.length === 0) return items;
      const itemMap = new Map(items.map(l => [l.id || l.key, l]));
      const sorted = orderArray.map(key => itemMap.get(key)).filter(Boolean);
      const unsorted = items.filter(l => !orderArray.includes(l.id || l.key));
      return [...sorted, ...unsorted];
    };

    // Создаем группы как объекты, фильтруя по статусу удаления
    const groupsArray = Object.entries(layerGroups)
      .filter(([id, group]) => showDeleted ? group.deleted : !group.deleted)
      .map(([id, group]) => ({
        ...group,
        key: id,
        id,
        type: 'group',
      }));

    // Отделяем основные слои от масок
    const filteredLayers = layers.filter(layer => showDeleted ? layer.deleted : !layer.deleted);
    const mainLayers = filteredLayers.filter(layer => !layer.clippingFor);
    const maskLayers = filteredLayers.filter(layer => layer.clippingFor);

    const layersInGroups = {};
    const layersWithoutGroup = [];

    // Группируем только основные слои (не маски)
    mainLayers.forEach(layer => {
      const layerSettings = getLayerSettings(layer.id);
      if (layerSettings.groupId && layerGroups[layerSettings.groupId] && 
          (showDeleted ? layerGroups[layerSettings.groupId].deleted : !layerGroups[layerSettings.groupId].deleted)) {
        layersInGroups[layerSettings.groupId] ??= [];
        layersInGroups[layerSettings.groupId].push(layer);
      } else {
        layersWithoutGroup.push(layer);
      }
    });

    // Создаем единый массив для сортировки групп и слоев верхнего уровня
    const topLevelItems = [...groupsArray, ...layersWithoutGroup];
    const sortedTopLevelItems = applySortOrder(topLevelItems, actualLayerOrder);

    const finalStructure = [];
    
    // Функция для добавления слоя с его масками
    const addLayerWithMasks = (layer) => {
      // Получаем все маски этого слоя
      const attachedMasks = maskLayers
        .filter(mask => mask.clippingFor === layer.id)
        .sort((a, b) => b.order - a.order);
      
      // Маски должны наследовать группу от основного слоя
      const layerSettings = getLayerSettings(layer.id);
      attachedMasks.forEach(mask => {
        if (layerSettings.groupId) {
          mask.inheritedGroupId = layerSettings.groupId;
        }
      });
      
      // Сначала добавляем маски (они отображаются выше основного слоя)
      attachedMasks.forEach(mask => {
        finalStructure.push(mask);
      });
      
      // Потом добавляем основной слой
      finalStructure.push(layer);
    };

    sortedTopLevelItems.forEach(item => {
      if (item.type === 'group') {
        finalStructure.push(item);
        // Удаленные группы всегда раскрыты, обычные группы по своему флагу expanded
        if (showDeleted ? true : item.expanded) {
          const sortedLayersInGroup = applySortOrder(layersInGroups[item.id] || [], actualLayerOrder);
          sortedLayersInGroup.forEach(layer => {
            addLayerWithMasks(layer);
          });
        }
      } else {
        addLayerWithMasks(item);
      }
    });

    return finalStructure;
  };

  // Собираем все слои (в зависимости от режима отображения)
  const getAllLayersArray = () => {
    const actualLayerOrder = getActualLayerOrder();
    const layersById = Object.keys(convy.layerInfo)
      .reduce((acc, id) => {
        acc[id] = { id, ...convy.layerInfo[id] };
        return acc;
      }, {});
    
    // Сортируем согласно layerOrder
    const sortedLayers = actualLayerOrder
      .map(id => layersById[id])
      .filter(Boolean);
    
    // Добавляем слои, которых нет в layerOrder
    const remainingLayers = Object.keys(layersById)
      .filter(id => !actualLayerOrder.includes(id))
      .map(id => layersById[id]);
    
    return [...sortedLayers, ...remainingLayers];
  };

  const allLayersArray = getAllLayersArray();

  const hasDeletedLayers = Object.values(convy.layerInfo).some(layer => layer.deleted) || 
                         Object.values(layerGroups).some(group => group.deleted);

  // Используем новую структуру с группами
  const structuredLayers = structureLayersWithGroups(allLayersArray, showDeletedLayers);

  // Для совместимости с существующим кодом, получаем основные слои без clippingFor
  const mainLayers = allLayersArray.filter(layer => !layer.clippingFor)
    .filter(layerData => showDeletedLayers ? layerData.deleted : !layerData.deleted);

  // Функция для проверки принадлежности слоя к группе
  const isLayerInGroup = (layerId) => {
    const layer = structuredLayers.find(l => l.id === layerId);
    if (layer && layer.inheritedGroupId) {
      return layerGroups[layer.inheritedGroupId];
    }
    
    const layerSettings = getLayerSettings(layerId);
    return layerSettings.groupId && layerGroups[layerSettings.groupId];
  };

  // Функция удаления элемента (группы или слоя)
  const handleDeleteItem = (itemId) => {
    const item = structuredLayers.find(i => (i.id || i.key) === itemId);
    if (!item) return;

    const performDelete = () => {
      if (item.type === 'group') {
        // Удаляем группу и все слои в ней
        const groups = { ...layerGroups };
        groups[item.id] = { ...groups[item.id], deleted: true };

        // Помечаем все слои в группе как удаленные
        allLayersArray.forEach(layer => {
          const layerSettings = getLayerSettings(layer.id);
          if (layerSettings.groupId === item.id && !layer.deleted) {
            convy.layerInfo[layer.id].deleted = true;
          }
        });

        saveGameSets({ 
          layerGroups: groups,
          layerInfo: convy.layerInfo 
        });
        showTemporaryHint(window.t('layer_menu.group_deleted', { groupName: item.title }), { force: true });

      } else if (item.type !== 'group') {
        // Удаляем обычный слой
        const layer = convy.layerInfo[item.id];
        if (layer) {
          layer.deleted = true;
          saveGameSets({ layerInfo: convy.layerInfo });
          showTemporaryHint(window.t('layer_menu.layer_deleted', { layerName: layer.name || item.id }), { force: true });
        }
      }

      menu.methods.render();
      redraw();
      window.croco?.haptic?.();
    };

    showPopupMessage({
      title: window.t('layer_menu.delete_confirm_title', { name: item.title || item.name || item.id }),
      text: item.type === 'group' 
        ? window.t('layer_menu.delete_group_confirm_text', { groupName: item.title })
        : window.t('layer_menu.delete_layer_confirm_text', { layerName: item.name || item.id }),
      buttons: [
        { type: 'cancel', text: window.t('customize.button_cancel') },
        { id: 'delete', type: 'destructive', text: window.t('customize.button_delete_confirm') },
      ],
      callback: (buttonId) => {
        if (buttonId === 'delete') {
          performDelete();
        }
      },
    });
  };

  // Функция восстановления группы
  const handleRestoreGroup = (groupId, event) => {
    const group = layerGroups[groupId];
    if (!group) return;

    if (event) {
      // Если передан event, показываем всплывающее меню для подтверждения
      event.stopPropagation();
      showPopupMessage({
        title: window.t('layer_menu.restore_group_title'),
        message: window.t('layer_menu.restore_group_text', { groupName: group.title }),
        buttons: [
          { type: 'cancel', text: window.t('customize.button_cancel') },
          { id: 'restore', type: 'default', text: window.t('layer_menu.restore_group') },
        ],
        callback: (buttonId) => {
          if (buttonId === 'restore') {
            window.croco?.haptic?.();
            // Выполняем восстановление
            const groups = { ...layerGroups };
            groups[groupId] = { ...groups[groupId], deleted: false };
            
            // Восстанавливаем все слои в группе
            allLayersArray.forEach(layer => {
              const layerSettings = getLayerSettings(layer.id);
              if (layerSettings.groupId === groupId && layer.deleted) {
                convy.layerInfo[layer.id].deleted = false;
              }
            });

            saveGameSets({ 
              layerGroups: groups,
              layerInfo: convy.layerInfo 
            });
            showTemporaryHint(window.t('layer_menu.group_restored', { groupName: group.title }), { force: true });
            setShowDeletedLayers(false);
            menu.methods.render();
            redraw();
          }
        }
      });
    } else {
      // Если event не передан, выполняем восстановление напрямую (для обратной совместимости)
      const groups = { ...layerGroups };
      groups[groupId] = { ...groups[groupId], deleted: false };
      
      // Восстанавливаем все слои в группе
      allLayersArray.forEach(layer => {
        const layerSettings = getLayerSettings(layer.id);
        if (layerSettings.groupId === groupId && layer.deleted) {
          convy.layerInfo[layer.id].deleted = false;
        }
      });

      saveGameSets({ 
        layerGroups: groups,
        layerInfo: convy.layerInfo 
      });
      showTemporaryHint(window.t('layer_menu.group_restored', { groupName: group.title }), { force: true });
      setShowDeletedLayers(false);
      menu.methods.render();
      redraw();
    }
  };

  const strokesByLayer = {};
  const userStrokes = ref.history.userStrokes;

  for (const userId in userStrokes) {
    const strokesArray = userStrokes[userId];
    for (let i = 0, len = strokesArray.length; i < len; i++) {
      const stroke = strokesArray[i];
      const layerId = stroke.layerId || 'base';
      if (!strokesByLayer[layerId]) {
        strokesByLayer[layerId] = [];
      }
      strokesByLayer[layerId].push(stroke);
    }
  }

  const setActiveLayer = (layerId) => {
    if (brush.lastActionTime > Date.now() - 300) return;

    brush.lastActionTime = Date.now();
    const layer = convy.layerInfo[layerId];
    if (!layer?.visible) {
      return showTemporaryHint(window.t('layer_menu.cant_choose_invisible'), { force: true });
    }
    saveUserGameSets({ activeLayerId: layerId });
    menu.methods.render();
    showTemporaryHint(window.t('layer_menu.choosen_layer', { layerName: layer.name }), { force: true, duration: 500 });

    convy.transformGroupId = null;

  };

  const toggleMask = (layerId, type) => {

    brush.lastActionTime = Date.now();
    const layer = convy.layerInfo[layerId];

    if (layer.clippingFor) {

      const parentId = layer.clippingFor;
      delete layer.clippingFor;
      // Не используем больше layer.order, маска просто теряет связь с основным слоем
      showTemporaryHint(window.t('layer_menu.mask_removed', { layerName: layer.name || layerId }), { force: true });

    } else {

      const attachedMasksForLayer = allLayersArray.filter(l => l.clippingFor === layerId);
      if (attachedMasksForLayer.length > 0) {
        return showTemporaryHint(window.t('layer_menu.operation_cannot_be_done', { layerName: layer.name || layerId }), { force: true });
      }

      // Получаем группу текущего слоя
      const currentLayerSettings = getLayerSettings(layerId);
      const currentGroupId = currentLayerSettings.groupId || null;

      // Получаем актуальный порядок и фильтруем слои только той же группы  
      const actualLayerOrder = getActualLayerOrder();
      const layersInSameGroup = actualLayerOrder
        .map(id => allLayersArray.find(l => l.id === id))
        .filter(Boolean)
        .filter(l => {
          const layerSettings = getLayerSettings(l.id);
          return (layerSettings.groupId || null) === currentGroupId && !l.clippingFor;
        });

      const currentIndex = layersInSameGroup.findIndex(l => l.id === layerId);
      if (currentIndex === -1 || currentIndex === layersInSameGroup.length - 1) {
        return showTemporaryHint(window.t('layer_menu.operation_cannot_be_done'), { force: true });
      }

      const layerBelow = layersInSameGroup[currentIndex + 1];
      layer.clippingFor = layerBelow.id;
      showTemporaryHint(window.t('layer_menu.mask_applied', { layerName: layer.name || layerId }), { force: true });
    }
    redraw();
    menu.methods.render();
    menu.methods.appleHaptic?.('light');
  };

  const addNewLayer = () => {
    if (!premium.premiumFeatures.includes('layers') || user.premiumOff) {
      delayPremiumMenu('menu');
      return;
    }
    brush.lastActionTime = Date.now();

    // Получаем актуальный порядок слоев
    const currentLayerOrder = getActualLayerOrder();
    
    // Находим позицию активного слоя в layerOrder
    const activeLayerIndex = currentLayerOrder.indexOf(activeLayerId);
    
    if (activeLayerIndex === -1) {
      // Если активного слоя нет в порядке, создаем слой в начале
      const newLayer = convy.methods.createLayer();
      const newLayerOrder = [newLayer.id, ...currentLayerOrder];
      
      const activeLayerSettings = getLayerSettings(activeLayerId);
      if (activeLayerSettings.groupId) {
        saveLayerSettings(newLayer.id, { groupId: activeLayerSettings.groupId });
      }
      
      saveGameSets({ 
        layerInfo: convy.layerInfo,
        layerOrder: newLayerOrder 
      });
      saveUserGameSets({ activeLayerId: newLayer.id });
      menu.methods.render();
      showTemporaryHint(window.t('layer_menu.new_layer_created'), { force: true });
      menu.methods.appleHaptic?.('light');
      return;
    }

    // Создаем новый слой
    const newLayer = convy.methods.createLayer();

    // Если активный слой принадлежит группе, добавляем новый слой в ту же группу
    const activeLayerSettings = getLayerSettings(activeLayerId);
    if (activeLayerSettings.groupId) {
      saveLayerSettings(newLayer.id, { groupId: activeLayerSettings.groupId });
    }

    // Вставляем новый слой в layerOrder прямо перед активным слоем (выше него в UI)
    const newLayerOrder = [...currentLayerOrder];
    newLayerOrder.splice(activeLayerIndex, 0, newLayer.id);

    saveGameSets({ 
      layerInfo: convy.layerInfo,
      layerOrder: newLayerOrder 
    });
    saveUserGameSets({ activeLayerId: newLayer.id });
    menu.methods.render();
    showTemporaryHint(window.t('layer_menu.new_layer_created'), { force: true });
    menu.methods.appleHaptic?.('light');
  };

  const toggleVisibility = (layerId) => {
    brush.lastActionTime = Date.now();
    const layerInfo = convy.layerInfo[layerId];
    if (layerInfo) {
      layerInfo.visible = !layerInfo.visible;
      showTemporaryHint(
        window.t(layerInfo.visible ? 'layer_menu.visible' : 'layer_menu.invisible', { layerName: layerInfo.name }),
        { force: true }
      );
      redraw();
      menu.methods.appleHaptic?.('light');
    }
  };

  const openCustomizeLayer = (layerId, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    brush.lastActionTime = Date.now();
    menu.methods.openMenu('customizeLayer', { layerId });
    menu.methods.appleHaptic?.('light');
  };

  const handleRestoreLayer = (layerId, event) => {
    const layer = convy.layerInfo[layerId];
    if (!layer) return;
  
    if (event) {
      // Если передан event, показываем всплывающее меню для подтверждения
      event.stopPropagation();
      showPopupMessage({
        title: window.t('customize_layer.restore_layer_title'),
        message: window.t('customize_layer.restore_layer_text', { layerName: layer.name || layerId }),
        buttons: [
          { type: 'cancel', text: window.t('customize_layer.button_cancel') },
          { id: 'restore', type: 'default', text: window.t('customize_layer.restore_layer') },
        ],
        callback: (buttonId) => {
          if (buttonId === 'restore') {
            window.croco?.haptic?.();
            // Выполняем восстановление
            layer.deleted = false;
            showTemporaryHint(window.t('customize_layer.layer_restored', { layerName: layer.name || layerId }), { force: true });
            saveGameSets({ layerInfo: ref.convy.layerInfo });
            setShowDeletedLayers(false);
            menu.methods.render();
            redraw();
          }
        }
      });
    } else {
      // Если event не передан, выполняем восстановление напрямую
      layer.deleted = false;
      showTemporaryHint(window.t('customize_layer.layer_restored', { layerName: layer.name || layerId }), { force: true });
      saveGameSets({ layerInfo: ref.convy.layerInfo });
      setShowDeletedLayers(false);
      menu.methods.render();
      redraw();
    }
  };

  const previewColor = (userColor) => {
    setViewedColor(userColor);
    debounceGameSets({ backgroundColor: userColor });
    drawing.methods.redraw();
  };

  const pickColor = () => {
    debounceGameSets({ backgroundColor: viewedColor });
    setUserColorView(false);
    drawing.methods.redraw();
  };

  const handleSetTransformLayer = () => {
    const newValue = !user.transformLayer;
    saveUserSets({ transformLayer: newValue });
    if (newValue) {
      menu.methods.appleHaptic?.('light');
      showTemporaryHint(window.t('layer_menu.layer_transformation_is_on'), { force: true });
    }
  };

  // Функция включения/выключения режима трансформации группы
  const handleToggleTransformGroup = (groupId, event) => {
    event?.stopPropagation();
    brush.lastActionTime = Date.now();

    if (!premium.premiumFeatures.includes('layers') || user.premiumOff) {
      delayPremiumMenu('menu');
      return;
    }

    // Переключаем режим трансформации группы
    if (convy.transformGroupId === groupId) {
      // Выключаем режим трансформации
      convy.transformGroupId = null;
      showTemporaryHint(window.t('layer_menu.group_transform_off', { groupName: layerGroups[groupId]?.title }), { force: true });
    } else {
      // Включаем режим трансформации для этой группы
      convy.transformGroupId = groupId;
      showTemporaryHint(window.t('layer_menu.group_transform_on', { groupName: layerGroups[groupId]?.title }), { force: true });
    }

    menu.methods.render();
    menu.methods.appleHaptic?.('light');
  };

  const handleClose = () => {
    const initialLayers = initialAllLayersInfoRef.current;
    const currentLayers = convy.layerInfo;

    const initialLayerOrder = initialLayerOrderRef.current;
    const currentLayerOrder = getActualLayerOrder();
    const layerOrderChanged = JSON.stringify(initialLayerOrder) !== JSON.stringify(currentLayerOrder);

    const initialLayerGroups = initialLayerGroupsRef.current;
    const currentLayerGroups = gameSettings.layerGroups || {};
    const layerGroupsChanged = JSON.stringify(initialLayerGroups) !== JSON.stringify(currentLayerGroups);
    
    // Проверяем изменение backgroundColor
    const initialBackgroundColor = initialBackgroundColorRef.current;
    const currentBackgroundColor = info.game.settings.backgroundColor || '#FFFFFFFF';
    const backgroundColorChanged = initialBackgroundColor !== currentBackgroundColor;
    
    // Инициализируем структуры данных сразу
    const prevData = {};
    const infoData = {};
    const changedLayerIds = [];
    
    // Находим измененные слои и сразу заполняем данные
    Object.keys(currentLayers).forEach(layerId => {
      const initial = initialLayers[layerId];
      const current = currentLayers[layerId];
      
      if (!initial || JSON.stringify(initial) !== JSON.stringify(current)) {
        if (initial) {
          prevData[layerId] = JSON.parse(JSON.stringify(initial));
        }
        infoData[layerId] = JSON.parse(JSON.stringify(current));
        changedLayerIds.push(layerId);
      }
    });

    // Если есть изменения слоев - создаем штрих трансформации
    if (changedLayerIds.length > 0 || layerOrderChanged || layerGroupsChanged) {
      // Получаем текущий порядок слоев для передачи в функцию
      const gameSettings = info.game.settings || {};
      const currentLayerOrder = gameSettings.layerOrder || [];
      
      ref.history.methods.newTransformationStroke({
        layerIds: changedLayerIds,
        prev: prevData,
        info: infoData,
        note: 'layer menu changes',
        
        layerOrder: layerOrderChanged ? currentLayerOrder : null, 
        layerGroups: layerGroupsChanged ? currentLayerGroups : null, 
        tech: true,
      });
    }

    // Если изменился backgroundColor - создаем отдельный штрих
    if (backgroundColorChanged) {
      ref.history.methods.newBackgroundChangeStroke({
        prev: initialBackgroundColor,
        color: currentBackgroundColor,
      });
    }
    
    closeMenu();
  };

  // Функция разгруппировки и сортировки слоя
  const handleUngroupAndSortLayer = (layerId, position) => {
    const layerSettings = getLayerSettings(layerId);
    let changed = false;

    // Разгруппировка
    if (layerSettings.groupId) {
      layerSettings.groupId = null;
      saveLayerSettings(layerId, layerSettings);
      showTemporaryHint(window.t('layer_menu.layer_ungrouped', { layerName: convy.layerInfo[layerId]?.name || layerId }), { force: true });
      changed = true;
    }
    
    // Сортировка
    const currentLayerOrder = getActualLayerOrder();
    const newLayerOrder = currentLayerOrder.filter(id => id !== layerId);

    if (position === 'top') {
      newLayerOrder.unshift(layerId);
    } else { // bottom
      newLayerOrder.push(layerId);
    }
    
    saveGameSets({ layerOrder: newLayerOrder });
    
    if (changed) {
      setTimeout(() => menu.methods.render(), 50);
    }
    window.croco?.haptic?.();
    setTimeout(() => drawing.methods.redraw(), 50);
  };

  // Функция разгруппировки слоя
  const handleUngroupLayer = (layerId) => {
    const layerSettings = getLayerSettings(layerId);
    if (layerSettings.groupId) {
      layerSettings.groupId = null;
      saveLayerSettings(layerId, layerSettings);
      showTemporaryHint(window.t('layer_menu.layer_ungrouped', { layerName: convy.layerInfo[layerId]?.name || layerId }), { force: true });
      setTimeout(() => menu.methods.render(), 50);
      window.croco?.haptic?.();
    }
  };

  const { draggingId, dragOverId, dragDirection, ghostPosition, itemRefs, handlePointerDownToStartDrag, isDraggingActive } = useDragAndDropSort({
    items: structuredLayers,
    onSort: (updatedItems) => {
      // Это предотвратит срабатывание клика и закрытие меню
      brush.lastActionTime = Date.now();

      // Получаем актуальный порядок и обновляем его
      const currentLayerOrder = getActualLayerOrder();
      const finalLayerOrder = updatedItems.map(i => i.id || i.key);
      
      // Добавляем элементы, которых нет в новом порядке, но есть в старом
      const missingItems = currentLayerOrder.filter(id => !finalLayerOrder.includes(id));
      const completeLayerOrder = [...finalLayerOrder, ...missingItems];
      
      saveGameSets({ layerOrder: completeLayerOrder });
      menu.methods.appleHaptic?.('light');
      setTimeout(() => drawing.methods.redraw(), 50);

    },
    onMove: (draggedId, targetId) => {
      // Это предотвратит срабатывание клика и закрытие меню
      brush.lastActionTime = Date.now();

      const targetItem = structuredLayers.find(i => (i.id || i.key) === targetId);
      const draggedItem = structuredLayers.find(i => (i.id || i.key) === draggedId);
     
      // Маски не могут перемещаться самостоятельно - они всегда следуют за основным слоем
      if (draggedItem && convy.layerInfo[draggedId]?.clippingFor) {
        showTemporaryHint(window.t('layer_menu.cannot_move_mask'), { force: true });
        return true;
      }

      // Перемещение слоя в группу
      if (draggedItem?.type !== 'group' && targetItem?.type === 'group') {
        const layerSettings = getLayerSettings(draggedId);
        layerSettings.groupId = targetId;
        saveLayerSettings(draggedId, layerSettings);

        setTimeout(() => drawing.methods.redraw(), 50);
        return true; // Сообщаем хуку, что мы обработали событие
      }

      // Перемещение слоя к другому слою (наследует ее groupId)
      if (draggedItem?.type !== 'group' && targetItem?.type !== 'group') {
        const targetLayerSettings = getLayerSettings(targetId);
        const targetGroupId = targetLayerSettings.groupId || null;
        
        
        const draggedLayerSettings = getLayerSettings(draggedId);
        draggedLayerSettings.groupId = targetGroupId;
        saveLayerSettings(draggedId, draggedLayerSettings);

        setTimeout(() => drawing.methods.redraw(), 50);
        return false; 
      }

      return false; // Мы не обрабатываем это событие, хук должен заняться сортировкой
    },
    containerRef: layerMenuRef,
    keyExtractor: (item) => item.id || item.key,
    dragThreshold: 0,
    deleteZoneRef,
    onDelete: handleDeleteItem,
    setIsOverDeleteZone,
    ungroupZoneRef,
    onUngroup: (layerId) => handleUngroupAndSortLayer(layerId, 'bottom'),
    setIsOverUngroupZone,
    ungroupZoneTopRef,
    onUngroupTop: (layerId) => handleUngroupAndSortLayer(layerId, 'top'),
    setIsOverUngroupZoneTop,
    needToStopPropagation: true,
  });

  // Функция рендеринга строки активного слоя
  const renderLayerRow = (commonLayer) => {
    const layerSettings = getLayerSettings(commonLayer.id);
    const isPartOfTransformGroup = convy.transformGroupId && 
      (layerSettings.groupId === convy.transformGroupId || commonLayer.inheritedGroupId === convy.transformGroupId);

    const isActiveLayer = commonLayer.visible && (
      isPartOfTransformGroup || 
      (!convy.transformGroupId && activeLayerId === commonLayer.id)
    );
    const isInGroup = isLayerInGroup(commonLayer.id) || commonLayer.inheritedGroupId;

    return (
      <div
        key={commonLayer.id}
        ref={el => itemRefs.current[commonLayer.id] = el}
        data-draggable-id={commonLayer.id}
        className={`layer-row ${!commonLayer.visible ? 'invisible' : isActiveLayer ? 'active' : ''} ${isInGroup ? 'in-group' : ''} ${isDraggingActive && draggingId === commonLayer.id ? 'dragging' : ''} ${dragOverId === commonLayer.id ? `drag-over drag-over-${dragDirection}` : ''}`}
        style={commonLayer.clippingFor ? { marginLeft: '10%', width: '90%' } : {}}
        onClick={() => setActiveLayer(commonLayer.id)}
      >
        <div className="drag-handle" onPointerDown={(e) => handlePointerDownToStartDrag(e, commonLayer.id)}>
          {commonLayer.clippingFor ? '⮦' : '☰'}
        </div>
        <div style={{ touchAction: 'none', cursor: 'move' }} onPointerDown={(e) => handlePointerDownToStartDrag(e, commonLayer.id)}>
          <LayerThumbnail layerId={commonLayer.id} store={store} />
        </div>
        <div className="layer-row-column">
          <div className="layer-row-main">
            <span className="layer-name">{commonLayer.name || commonLayer.id}</span>
          </div>
          <div className="layer-metrics">
            {ref.convy.methods.layerMetrics(commonLayer.id, strokesByLayer[commonLayer.id])}
          </div>
        </div>
        <button
          className={`layer-control-button ${isActiveLayer ? 'active' : ''}`}
          onClick={(e) => { e.stopPropagation(); toggleMask(commonLayer.id); }}
          {...createBetterClick({ onClick: (e) => { e.stopPropagation(); toggleMask(commonLayer.id); } })}
        >
          {commonLayer.clippingFor ? (
            commonLayer.erasingMask ? (
              <FaEraser className="layer-control-icon" />
            ) : (
              <FaTimes className="layer-control-icon" />
            )
          ) : (
            <FaArrowDown className="layer-control-icon" />
          )}
        </button>
        <button
          className={`layer-control-button ${isActiveLayer ? 'active' : ''}`}
          onClick={(e) => { e.stopPropagation(); toggleVisibility(commonLayer.id); }}
          {...createBetterClick({ onClick: (e) => { e.stopPropagation(); toggleVisibility(commonLayer.id); } })}
        >
          {commonLayer.visible ? 
            <FaEye className="layer-control-icon" /> : 
            <FaEyeSlash className="layer-control-icon" />
          }
        </button>
        <button
          className={`layer-control-button ${isActiveLayer ? 'active' : ''}`}
          {...createBetterClick({ onClick: (e) => { e.stopPropagation(); openCustomizeLayer(commonLayer.id, e); } })}
        >
          <FaCog className="layer-control-icon" />
        </button>
      </div>
    );
  };

  // Функция рендеринга строки удаленного слоя
  const renderDeletedLayerRow = (commonLayer) => {
    const isInGroup = isLayerInGroup(commonLayer.id) || commonLayer.inheritedGroupId;
    
    return (
      <div
        key={commonLayer.id}
        className={`layer-row deleted ${isInGroup ? 'in-group' : ''}`}
        style={commonLayer.clippingFor ? { marginLeft: '10%', width: '90%' } : {}}
        onClick={(e) => handleRestoreLayer(commonLayer.id, e)}
      >
        <div className="drag-handle">
          {commonLayer.clippingFor ? '⮦' : '☰'}
        </div>
        <LayerThumbnail layerId={commonLayer.id} store={store} />
        <div className="layer-row-column">
          <div className="layer-row-main">
            <span className="layer-name">{commonLayer.name || commonLayer.id}</span>
          </div>
          <div className="layer-metrics">
            {ref.convy.methods.layerMetrics(commonLayer.id, strokesByLayer[commonLayer.id])}
          </div>
        </div>
      </div>
    );
  };

  // Функция рендеринга строки удаленной группы
  const renderDeletedGroupRow = (groupItem) => {
    return (
      <div
        key={groupItem.id}
        className={`layer-group-row deleted`}
        onClick={(e) => handleRestoreGroup(groupItem.id, e)}
        {...createBetterClick({ onClick: (e) => handleRestoreGroup(groupItem.id, e) })}
      >
        <span className="layer-group-toggle">
          <FaMinus />
        </span>
        <span className="layer-group-label">
          {groupItem.title}
          <span className="layer-group-label-count">
            ({layersInGroupsCount()[groupItem.id] || 0})
          </span>
        </span>
        <div className="layer-drag-handle" style={{fontSize: '18px'}}>
          ☰
        </div>
      </div>
    );
  };

  const renderGroupRow = (groupItem) => {
    const isTransformActive = convy.transformGroupId === groupItem.id;

    return (
      <div
        key={groupItem.id}
        ref={el => (itemRefs.current[groupItem.id] = el)}
        data-draggable-id={groupItem.id}
        className={`layer-group-row ${groupItem.expanded ? 'expanded' : ''} ${!groupItem.visible && groupItem.visible !== undefined ? 'invisible' : ''} ${isTransformActive ? 'transform-active' : ''} ${isDraggingActive && draggingId === groupItem.id ? 'dragging' : ''} ${dragOverId === groupItem.id && structuredLayers.find(i=>(i.id || i.key) === draggingId)?.type !== 'group' ? 'drag-over-group' : ''} ${dragOverId === groupItem.id && !(structuredLayers.find(i=>(i.id || i.key) === draggingId)?.type !== 'group') ? `drag-over drag-over-${dragDirection}` : ''}`}
        onClick={() => handleToggleLayerGroup(groupItem.id)}
        {...createBetterClick({ onClick: () => handleToggleLayerGroup(groupItem.id) })}
      >
        <span className="layer-group-toggle">
          {groupItem.expanded ? <FaMinus /> : <FaPlus />}
        </span>
        <span className="layer-group-label">
          {groupItem.title}
          <span className="layer-group-label-count">
            ({layersInGroupsCount()[groupItem.id] || 0})
          </span>
        </span>
        <button
          className={`layer-control-button ${isTransformActive ? 'active' : ''}`}
          onClick={(e) => handleToggleTransformGroup(groupItem.id, e)}
          {...createBetterClick({ onClick: (e) => handleToggleTransformGroup(groupItem.id, e) })}
        >
          <FaArrowsTurnToDots className="layer-control-icon" />
        </button>
        <button
          className={`layer-control-button ${isTransformActive ? 'active' : ''}`}
          onClick={(e) => { e.stopPropagation(); toggleGroupVisibility(groupItem.id); }}
          {...createBetterClick({ onClick: (e) => { e.stopPropagation(); toggleGroupVisibility(groupItem.id); } })}
        >
          {groupItem.visible !== false ? 
            <FaEye className="layer-control-icon" /> : 
            <FaEyeSlash className="layer-control-icon" />
          }
        </button>
        <div className="layer-drag-handle" style={{fontSize: '20px'}} onPointerDown={(e) => handlePointerDownToStartDrag(e, groupItem.id)}>
          ☰
        </div>
      </div>
    )
  }

  
  return (
    <>
      <div className="overlay-background" onClick={handleClose} style={{ opacity: 0 }}></div>
      <div className="layer-menu" ref={layerMenuRef}>
        <div className="layer-menu-header">
          <span className="layer-menu-title">
            {showDeletedLayers ? window.t('layer_menu.deleted_title') : window.t('layer_menu.title')}
          </span>
          <div className="layer-menu-buttons">
            <div 
              ref={deleteZoneRef}
              className={`new-layer-button ${isOverDeleteZone ? 'active' : ''} ${isDraggingActive ? 'visible delete-zone' : hasDeletedLayers ? '' : 'hidden'}`}
              style={{
                backgroundColor: isDraggingActive ? (isOverDeleteZone ? '#ff4444' : '#ff6666') : 'var(--tg-theme-button-color)',
                opacity: isDraggingActive ? 1 : (hasDeletedLayers ? 0.5 : 0),
                pointerEvents: isDraggingActive || hasDeletedLayers ? 'auto' : 'none',
              }}
              {...createBetterClick({ onClick: () => { if (!isDraggingActive) setShowDeletedLayers(!showDeletedLayers); } })}
            >
              {isDraggingActive ? <FaTrash /> : (showDeletedLayers ? <TbArrowBigLeftLines /> : <FaTrash />)}
            </div>

            {!showDeletedLayers && (
              <>
                <button 
                  className="new-layer-button" 
                  {...createBetterClick({ onClick: handleCreateLayerGroup })}
                >
                  <FaFolderPlus />
                </button>
                <button 
                  className="new-layer-button" 
                  {...createBetterClick({ onClick: addNewLayer })}
                >
                  <FaPlus />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="layer-list add-scroll touch-active">
          {/* Область "Без группы" для разгруппировки слоев (ВЕРХ) */}
          {!showDeletedLayers && 
            (
            <div 
              ref={ungroupZoneTopRef}
              className={`ungroup-drop-zone ${isOverUngroupZoneTop ? 'active' : isDraggingActive ? 'visible' : ''}`}
            >
              { isOverUngroupZoneTop && (<span className="ungroup-zone-text">{window.t('layer_menu.ungroup_zone_top')}</span>)}
              
            </div>
          )}

          {structuredLayers.map(item => {
            if (item.type === 'group') {
              if (showDeletedLayers) {
                return renderDeletedGroupRow(item);
              } else {
                return renderGroupRow(item);
              }
            } else {
              // Все слои (включая маски) уже в правильном порядке
              return showDeletedLayers ? renderDeletedLayerRow(item) : renderLayerRow(item);
            }
          })}
          
          {/* Область "Без группы" для разгруппировки слоев (НИЗ) */}
          {!showDeletedLayers && 
            (
            <div 
              ref={ungroupZoneRef}
              className={`ungroup-drop-zone ${isOverUngroupZone ? 'active' : isDraggingActive ? 'visible' : ''}`}
            >
              { isOverUngroupZone && 
              (<span className="ungroup-zone-text">{window.t('layer_menu.ungroup_zone_bottom')}</span>)}
            </div>
          )}
        </div>

        <div className="divider"></div>
        <div className="settings-item">
          <label className="settings-label" style={{ opacity: user.transformLayer ? 1 : 0.5 }}>
            <input
              type="checkbox"
              className="word-menu-item-checkbox"
              checked={user.transformLayer}
              onChange={handleSetTransformLayer}
              style={{ height: '20px' }}
            />
            <span>{window.t('layer_menu.transform_layer')}</span>
          </label>
        </div>
        <div 
          className="settings-item" 
          onClick={() => setUserColorView(true)}
        >
          <label className="settings-label" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <span className="settings-color-indicator" style={{
              backgroundColor: info.game.settings.backgroundColor || '#FFFFFFFF',
            }}></span>
            <span>{window.t('layer_menu.background_color')}</span>
          </label>
        </div>

        {isDraggingActive && draggingId && !showDeletedLayers && (
          <div
            className="layer-row ghost"
            style={{
              position: 'fixed',
              left: ghostPosition.x - 10,
              top: ghostPosition.y - 20,
              pointerEvents: 'none',
              width: itemRefs.current[draggingId]?.offsetWidth,
              zIndex: 1000,
            }}
          >
            {structuredLayers.find(i => (i.id || i.key) === draggingId)?.type === 'group' ? (
              <>
                <span className="layer-group-toggle">☰</span>
                <span className="layer-name">{layerGroups[draggingId]?.title || draggingId}</span>
              </>
            ) : (
              <>
                <div className="drag-handle">☰</div>
                <LayerThumbnail
                  layerId={draggingId}
                  store={store}
                />
                <span className="layer-name">{convy.layerInfo[draggingId]?.name || draggingId}</span>
              </>
            )}
          </div>
        )}

        {userColorView && (
          <>
            <div className="overlay-background" style={{ zIndex: 1400, opacity: 0 }} onClick={() => setUserColorView(false)}></div>
            <div 
              className="settings-color-picker-container" 
              style={{
                position: 'absolute',
                top: 'calc(min(50%, 100% - 140px))',
              }}
            >
              <HexAlphaColorPicker 
                color={viewedColor} 
                onChange={(userColor) => previewColor(userColor)}
              />
              <button className="wide-button" onClick={pickColor} style={{ marginTop: '10px' }}>
                {window.t('settings.set_color')}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default LayerMenu;